import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { draftToPayload, useCreateImmunization, useImmunizations } from 'hooks/useImmunizations';
import type { ImmunizationDraft, UpdateImmunizationPayload } from 'types/immunization';
import { getApiErrorMessage } from 'utils/apiError';

interface UseImmunizationEditorProps {
  interactionId?: string;
  medatixxPatientId?: string | null;
  onUserEdit?: () => void;
}

/** Edits of the immunization block. The export waits for the edits still in flight, so the
 * package never carries a batch number the block has already replaced. */
export function useImmunizationEditor({
  interactionId,
  medatixxPatientId,
  onUserEdit,
}: UseImmunizationEditorProps) {
  const { t } = useTranslation();
  const { immunizations, isLoading, update, remove } = useImmunizations(
    interactionId,
    medatixxPatientId
  );
  const { mutateAsync: updateAsync } = update;
  const { mutateAsync: removeAsync } = remove;
  const { mutateAsync: createAsync } = useCreateImmunization();
  const inFlight = useRef(new Set<Promise<boolean>>());

  const track = useCallback(
    (edit: Promise<unknown>) => {
      const settled: Promise<boolean> = edit.then(
        () => {
          onUserEdit?.();
          return true;
        },
        (error: Error) => {
          toast.error(getApiErrorMessage(error, t));
          return false;
        }
      );
      inFlight.current.add(settled);
      void settled.then(() => inFlight.current.delete(settled));
      return settled;
    },
    [onUserEdit, t]
  );

  const updateImmunization = useCallback(
    (id: number, data: UpdateImmunizationPayload) => track(updateAsync({ id, data })),
    [track, updateAsync]
  );

  const removeImmunization = useCallback(
    (id: number) => {
      void track(removeAsync(id));
    },
    [track, removeAsync]
  );

  /** False when the backend rejected it; the toast is already shown. */
  const createImmunization = useCallback(
    (draft: ImmunizationDraft) => {
      if (!interactionId || !medatixxPatientId) return Promise.resolve(false);
      return track(createAsync(draftToPayload(draft, interactionId, medatixxPatientId)));
    },
    [track, createAsync, interactionId, medatixxPatientId]
  );

  const flushPendingEdits = useCallback(
    () => Promise.all([...inFlight.current]).then((results) => results.every(Boolean)),
    []
  );

  return {
    immunizations,
    isLoading,
    createImmunization,
    updateImmunization,
    removeImmunization,
    flushPendingEdits,
  };
}
